import { Type } from 'class-transformer';
import type dayjs from 'dayjs';
import { Partner } from './partner';
import { Model } from './model';
import { User } from './user';
import { Invoice } from './invoice';
import { Classification } from './classification';
import { OutreachWorkflow, OutreachWorkflowRun } from './outreach_workflow';
import { TransformStringToDayJS } from './utils';
import { Task } from './task';
import { type Direction } from './activity';

export enum CallStatus {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  PENDING_APPROVAL = 'pending_approval',
  QUEUED = 'queued',
  REGISTERED = 'registered',
  INITIATED = 'initiated',
  RINGING = 'ringing',
  CANCELED = 'canceled',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  BUSY = 'busy',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  NO_ANSWER = 'no_answer',
  FAILED = 'failed',
  AWAITING_POST_PROCESSING = 'awaiting_post_processing',
}

export enum CallSentiment {
  POSITIVE = 'positive',

  NEGATIVE = 'negative',

  NEUTRAL = 'neutral',

  UNKNOWN = 'unknown',
}

export enum CallProvider {
  LIVEKIT = 'livekit',
  RETELL = 'retell',
}

export class Call extends Model {
  @Type(() => User)
  approver: User | null;

  @Type(() => Classification)
  classifications: Classification[];

  @TransformStringToDayJS()
  start_time: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  end_time: dayjs.Dayjs | null;

  call_reason: string | null;

  custom_classifications: string[] | null;

  direction: Direction;

  disconnection_reason: string | null;

  from_number: string;

  id: string;

  @Type(() => Invoice)
  invoices: Invoice[];

  in_voicemail: boolean;

  partner_id: string;

  @Type(() => Partner)
  partner: Partner | null;

  provider: CallProvider | null;

  recording_url: string | null;

  sentiment: CallSentiment | null;

  status: CallStatus;

  summary: string;

  to_number: string;

  @Type(() => Task)
  tasks: Task[];

  @Type(() => Transcript)
  transcript: Transcript[] | null;

  transcript_str: string | null;

  transferred_to: string | null;

  @Type(() => OutreachWorkflow)
  workflow: OutreachWorkflow;

  @Type(() => OutreachWorkflowRun)
  workflow_run: OutreachWorkflowRun | null;

  processing_status?: string;

  get isProcessing(): boolean {
    return !!this.processing_status && this.processing_status !== 'completed';
  }

  get processingLabel(): string {
    switch (this.processing_status) {
      case 'processing':
        return 'Processing call...';
      case 'classified':
        return 'Analyzed, creating tasks...';
      case 'task_created':
        return 'Task created';
      default:
        return 'Processing call...';
    }
  }

  get duration(): number {
    if (this.start_time && this.end_time) return this.end_time.diff(this.start_time, 'seconds');

    return 0;
  }

  get isInProgress(): boolean {
    return (
      this.status === CallStatus.REGISTERED ||
      this.status === CallStatus.INITIATED ||
      this.status === CallStatus.RINGING ||
      this.status === CallStatus.IN_PROGRESS
    );
  }

  get invoicesWithPdfIds() {
    return this.invoices?.filter((inv) => inv.pdf_id) || [];
  }

  get openTasks(): Task[] {
    return this.tasks.filter((task) => task.isOpen);
  }

  get closedTasks(): Task[] {
    return this.tasks.filter((task) => task.isClosed);
  }

  get completedTasks(): Task[] {
    return this.tasks.filter((task) => task.isCompleted);
  }

  get openResponseTasks(): Task[] {
    return this.openTasks?.filter((task) => task.isReplyRequired);
  }

  get oldestOpenResponseTask(): Task {
    return this.openResponseTasks?.sort((a, b) => a?.created_at.diff(b?.created_at))[0];
  }

  get durationMs(): number {
    if (this.start_time && this.end_time)
      return this.end_time.diff(this.start_time, 'milliseconds');

    return 0;
  }
}

export class Transcript extends Model {
  content: string;

  role: TranscriptRole;

  @Type(() => TranscriptWord)
  words: TranscriptWord[];
}

export enum TranscriptRole {
  AGENT = 'agent',
  USER = 'user',
  TRANSFER_TARGET = 'transfer_target',
}

export class TranscriptWord {
  end: number;

  start: number;

  word: string;
}
